import FirestoreClient from '../clients/FirestoreClient.js';
import GuideBookCacheService from '../services/GuideBookCacheService.js';
import GuideBookSearchService from '../services/GuideBookSearchService.js';
import GuideBookMetadataService from '../services/GuideBookMetadataService.js';
import errorHandler from '../handlers/ErrorHandler.js';
import { withTimeout } from '../utils/withTimeout.js';
import ko from 'knockout';

/**
 * GuideBookViewModel - Manages guidebook and FAQ content display and search
 * Orchestrates cache, search, and metadata services for organized content management
 * 
 * @class GuideBookViewModel
 */
class GuideBookViewModel {
    constructor(currentLangObservable, guideSearchQuery, faqsSearchQuery) {
        this.currentLang = currentLangObservable;
        this.guideSearchQuery = guideSearchQuery || ko.observable('');
        this.faqsSearchQuery = faqsSearchQuery || ko.observable('');
        this.currentProductId = null;
        this.currentProductDoc = null;

        // Initialize services
        this.cacheService = new GuideBookCacheService();
        this.searchService = new GuideBookSearchService(this.cacheService);
        this.metadataService = new GuideBookMetadataService();

        // Track which language content was cached in (for cache invalidation on language change)
        this._cachedLanguage = null;

        // Metadata (shared guides and faqs categories)
        this.guideMetadata = ko.observable(null);
        this.guidesArray = ko.observableArray([]);
        this.faqsArray = ko.observableArray([]);

        // Available categories (filtered based on what exists in product document)
        this.availableGuides = ko.observableArray([]);
        this.availableFaqs = ko.observableArray([]);

        // Selected content
        this.selectedGuideContent = ko.observable('');
        this.selectedFaqContent = ko.observable('');

        // Selected category
        this.selectedGuideCategory = ko.observable(null);
        this.selectedFaqsCategory = ko.observable(null);

        // Loading states
        this.isLoadingMetadata = ko.observable(false);
        this.isLoadingContent = ko.observable(false);
        this.isPreloadingContent = ko.observable(false);

        // Error states for user feedback
        this.guideLoadError = ko.observable(null);
        this.faqLoadError = ko.observable(null);

        // Filtered results (computed based on search query)
        this.filteredGuides = ko.pureComputed(() => {
            const filtered = this.searchService.filterCategories(
                this.availableGuides(),
                this.guideSearchQuery(),
                (item) => item.title,
                (item) => item.description,
                (item) => this.#getContentForSearch('guide', item.id)
            );
            // Sort by order field from metadata
            return filtered.sort((a, b) => (a.order || 999) - (b.order || 999));
        });

        this.filteredFaqs = ko.pureComputed(() => {
            const filtered = this.searchService.filterCategories(
                this.availableFaqs(),
                this.faqsSearchQuery(),
                (item) => item.title,
                (item) => item.description,
                (item) => this.#getContentForSearch('faq', item.id)
            );
            // Sort by order field from metadata
            return filtered.sort((a, b) => (a.order || 999) - (b.order || 999));
        });

        // While searching, open the first matching category unless the open one still matches
        this.filteredGuides.subscribe(list => this.#openFirstMatch(
            list, this.guideSearchQuery(), this.selectedGuideCategory(), id => this.loadGuideContent(null, id)));
        this.filteredFaqs.subscribe(list => this.#openFirstMatch(
            list, this.faqsSearchQuery(), this.selectedFaqsCategory(), id => this.loadFaqContent(null, id)));

        // Computed observable for loading message showing current category name
        this.loadingCategoryName = ko.computed(() => {
            const guideId = this.selectedGuideCategory();
            const faqId = this.selectedFaqsCategory();
            
            if (guideId) {
                const guide = (this.guidesArray() || []).find(g => g.id === guideId);
                return guide?.title || guideId;
            }
            if (faqId) {
                const faq = (this.faqsArray() || []).find(f => f.id === faqId);
                return faq?.title || faqId;
            }
            return 'content';
        });

        // Subscribe to language changes to refresh displayed content
        this.currentLang.subscribe(() => {
            this.#refreshSelectedContent();
        });
    }


    /**
     * Get cached content with language fallback
     * Returns content for current language, or falls back to default cached data
     * Marks the language this cache was retrieved for (for cache validation on language change)
     * @private
     */
    #getCachedContentByLanguage(cachedData) {
        const currentLang = this.currentLang() || 'en';
        this._cachedLanguage = currentLang;  // Mark which language we're using cache for
        return cachedData[currentLang] || cachedData;
    }

    /**
     * Open the first category of the search results, if the query is long enough
     * and the currently open category isn't among the results
     * @private
     */
    #openFirstMatch(results, query, selectedId, loadCategory) {
        if (!query || query.trim().length < this.searchService.MIN_SEARCH_LENGTH) return;
        if (!results.length) return;
        if (results.some(item => item.id === selectedId)) return;
        loadCategory(results[0].id);
    }

    /**
     * Get content for search purposes (private helper)
     * @private
     */
    #getContentForSearch(type, categoryId) {
        const cacheKey = this.cacheService.getCacheKey(this.currentProductId, type, categoryId);
        const cached = this.cacheService.getCachedContent(cacheKey);
        if (cached) {
            const langContent = this.#getCachedContentByLanguage(cached);
            return this.cacheService.extractHtmlContent(langContent);
        }
        return '';
    }

    /**
     * Refresh displayed content when language changes
     * @private
     */
    #refreshSelectedContent() {
        const selectedGuideId = this.selectedGuideCategory();
        const selectedFaqId = this.selectedFaqsCategory();

        if (selectedGuideId && this.currentProductId) {
            const cacheKey = this.cacheService.getCacheKey(this.currentProductId, 'guide', selectedGuideId);
            if (this.cacheService.isCached(cacheKey)) {
                const cachedData = this.cacheService.getCachedContent(cacheKey);
                const content = this.#getCachedContentByLanguage(cachedData);
                const html = this.cacheService.extractHtmlContent(content);
                const htmlString = typeof html === 'string' ? html : '';
                this.selectedGuideContent(htmlString);
            }
        }

        if (selectedFaqId && this.currentProductId) {
            const cacheKey = this.cacheService.getCacheKey(this.currentProductId, 'faq', selectedFaqId);
            if (this.cacheService.isCached(cacheKey)) {
                const cachedData = this.cacheService.getCachedContent(cacheKey);
                const content = this.#getCachedContentByLanguage(cachedData);
                const html = this.cacheService.extractHtmlContent(content);
                const htmlString = typeof html === 'string' ? html : '';
                this.selectedFaqContent(htmlString);
            }
        }
    }

    /**
     * Check if cached content is still valid for the current language
     * Returns false if language has changed since cache was loaded
     * @private
     */
    #isCacheValidForCurrentLanguage() {
        const currentLang = this.currentLang() || 'en';
        return this._cachedLanguage === currentLang;
    }

    /**
     * Set the current product slug (called when entering guidebook view)
     * Loads the product document and filters available categories
     * Note: Firestore docs are organized by product slug, not product ID
     */
    async setProductSlug(productSlug) {
        this.currentProductId = productSlug;
        
        try {
            // Load the product document to see what categories have content
            const productDoc = await withTimeout(
                FirestoreClient.getDocument('guidebooks', productSlug),
                10000,
                'setProductSlug: getDocument'
            );
            this.currentProductDoc = productDoc || {};
            
            // Filter guides: only include those with content in this product
            const guideContent = this.currentProductDoc.guideContent || {};
            const availableGuides = (this.guidesArray() || []).filter(guide => {
                return guideContent[guide.id];
            });
            this.availableGuides(availableGuides);
            
            // Filter FAQs: only include those with content in this product
            const faqContent = this.currentProductDoc.faqContent || {};
            const availableFaqs = (this.faqsArray() || []).filter(faq => {
                return faqContent[faq.id];
            });
            this.availableFaqs(availableFaqs);
            
            // Load all content for instant search
            await this.loadAllContent();
            
        } catch (error) {
            errorHandler.handle(error, 'GuideBookViewModel.setProductSlug', 'error');
            this.currentProductDoc = {};
            this.availableGuides([]);
            this.availableFaqs([]);
            this.isPreloadingContent(false);
        }
    }

    /**
     * Load all guide and FAQ content upfront for searching
     * Shows spinner while loading
     */
    async loadAllContent() {
        if (!this.currentProductId || !this.currentProductDoc) {
            return; // Missing currentProductId or currentProductDoc
        }

        this.isPreloadingContent(true);
        
        try {
            const productDoc = await withTimeout(
                FirestoreClient.getDocument('guidebooks', this.currentProductId),
                10000,
                'loadAllContent: getDocument'
            );
            if (!productDoc) {
                this.isPreloadingContent(false);
                return;
            }

            // Load all guide content
            const guideContent = productDoc.guideContent || {};
            for (const guideId of Object.keys(guideContent)) {
                const cacheKey = this.cacheService.getCacheKey(this.currentProductId, 'guide', guideId);
                if (!this.cacheService.isCached(cacheKey)) {
                    this.cacheService.setCachedContent(cacheKey, guideContent[guideId]);
                }
            }

            // Load all FAQ content
            const faqContent = productDoc.faqContent || {};
            for (const faqId of Object.keys(faqContent)) {
                const cacheKey = this.cacheService.getCacheKey(this.currentProductId, 'faq', faqId);
                if (!this.cacheService.isCached(cacheKey)) {
                    this.cacheService.setCachedContent(cacheKey, faqContent[faqId]);
                }
            }
        } catch (error) {
            errorHandler.handle(error, 'GuideBookViewModel.loadAllContent', 'error');
        } finally {
            this.isPreloadingContent(false);
        }
    }

    /**
     * Fetch the shared metadata (guides and faqs categories)
     */
    async loadMetadata() {
        if (this.isLoadingMetadata()) return;
        if (this.guideMetadata()) return; // Already loaded in this session

        // Check if metadata cached in persistent browser storage
        const cachedMetadata = this.metadataService.getPersistedMetadata('guidebook-metadata');
        if (cachedMetadata) {
            this.guideMetadata(cachedMetadata);
            this.guidesArray(cachedMetadata.guides || []);
            this.faqsArray(cachedMetadata.faqs || []);
            return;  // Use cached metadata, no network call needed
        }

        this.isLoadingMetadata(true);
        try {
            const metadata = await FirestoreClient.getDocument('guidebooks', '_metadata');
            if (metadata) {
                this.guideMetadata(metadata);
                this.guidesArray(metadata.guides || []);
                this.faqsArray(metadata.faqs || []);
                
                // Cache metadata in service
                this.metadataService.cacheMetadata(this.currentProductId || 'default', 'guide', metadata.guides || []);
                this.metadataService.cacheMetadata(this.currentProductId || 'default', 'faq', metadata.faqs || []);
                
                // Persist to browser storage for next session
                this.metadataService.persistMetadata('guidebook-metadata', metadata);
            }
        } catch (error) {
            errorHandler.handle(error, 'GuideBookViewModel.loadMetadata', 'error');
        } finally {
            this.isLoadingMetadata(false);
        }
    }
    /**
     * Fetch guide content for a specific category
     * @param {string} productSlug - The product slug (e.g., "product-1")
     * @param {string} guideId - The guide category ID (e.g., "getting-started")
     */
    async loadGuideContent(productSlug, guideId) {
        if (this.isLoadingContent()) return;

        const slug = this.currentProductId || productSlug;
        const cacheKey = this.cacheService.getCacheKey(slug, 'guide', guideId);

        // Set the selected category
        this.selectedGuideCategory(guideId);

        // Return cached content if available and language hasn't changed
        if (this.cacheService.isCached(cacheKey) && this.#isCacheValidForCurrentLanguage()) {
            const cachedData = this.cacheService.getCachedContent(cacheKey);
            const content = this.#getCachedContentByLanguage(cachedData);
            const html = this.cacheService.extractHtmlContent(content);
            const htmlString = typeof html === 'string' ? html : '';
            this.selectedGuideContent(htmlString);
            return;
        }

        this.isLoadingContent(true);
        try {
            // Load the parent product document
            const productDoc = await withTimeout(
                FirestoreClient.getDocument('guidebooks', slug),
                10000,
                'loadGuideContent: getDocument'
            );
            
            if (productDoc && productDoc.guideContent && productDoc.guideContent[guideId]) {
                const contentData = productDoc.guideContent[guideId];
                
                // Cache the content
                this.cacheService.setCachedContent(cacheKey, contentData);

                // Display content in current language
                const currentLang = this.currentLang() || 'en';
                const langContent = contentData[currentLang] || contentData;
                const html = this.cacheService.extractHtmlContent(langContent);
                const htmlString = typeof html === 'string' ? html : '';
                this.selectedGuideContent(htmlString);
                this.guideLoadError(null);  // Clear error when successful
            } else {
                this.selectedGuideContent('');
            }
        } catch (error) {
            this.#handleContentLoadError(error, 'guide', this.guideLoadError, this.selectedGuideContent, guideId);
        } finally {
            this.isLoadingContent(false);
        }
    }

    /**
     * Fetch FAQ content for a specific category
     * @param {string} productSlug - The product slug (e.g., "product-1")
     * @param {string} faqId - The FAQ category ID (e.g., "features")
     */
    async loadFaqContent(productSlug, faqId) {
        if (this.isLoadingContent()) return;

        const slug = this.currentProductId || productSlug;
        const cacheKey = this.cacheService.getCacheKey(slug, 'faq', faqId);

        // Set the selected category
        this.selectedFaqsCategory(faqId);

        // Return cached content if available and language hasn't changed
        if (this.cacheService.isCached(cacheKey) && this.#isCacheValidForCurrentLanguage()) {
            const cachedData = this.cacheService.getCachedContent(cacheKey);
            const content = this.#getCachedContentByLanguage(cachedData);
            const html = this.cacheService.extractHtmlContent(content);
            const htmlString = typeof html === 'string' ? html : '';
            this.selectedFaqContent(htmlString);
            return;
        }

        this.isLoadingContent(true);
        try {
            // Load the parent product document
            const productDoc = await withTimeout(
                FirestoreClient.getDocument('guidebooks', slug),
                10000,
                'loadFaqContent: getDocument'
            );
            
            if (productDoc && productDoc.faqContent && productDoc.faqContent[faqId]) {
                const contentData = productDoc.faqContent[faqId];
                
                // Cache the content
                this.cacheService.setCachedContent(cacheKey, contentData);

                // Display content in current language
                const currentLang = this.currentLang() || 'en';
                const langContent = contentData[currentLang] || contentData;
                const html = this.cacheService.extractHtmlContent(langContent);
                const htmlString = typeof html === 'string' ? html : '';
                this.selectedFaqContent(htmlString);
                this.faqLoadError(null);  // Clear error when successful
            } else {
                this.selectedFaqContent('');
            }
        } catch (error) {
            this.#handleContentLoadError(error, 'faq', this.faqLoadError, this.selectedFaqContent, faqId);
        } finally {
            this.isLoadingContent(false);
        }
    }

    /**
     * Handle content load errors with user feedback and retry capability
     * Consolidates error handling logic for guide and FAQ loads
     * @private
     * @param {Error} error - The caught error object
     * @param {string} contentType - 'guide' or 'faq'
     * @param {Function} errorObservable - ko.observable to set error state
     * @param {Function} contentObservable - ko.observable to clear content
     * @param {string} [categoryId] - Optional category ID for logging context
     */
    #handleContentLoadError(error, contentType, errorObservable, contentObservable, categoryId) {
        const operationName = contentType === 'guide' ? 'loadGuideContent' : 'loadFaqContent';
        const contextInfo = categoryId 
            ? { operation: operationName, categoryId } 
            : { operation: operationName };
        
        errorHandler.handle(error, contextInfo, 'error');
        
        const contentTypeLabel = contentType === 'guide' ? 'guide' : 'FAQ';
        const errorMessage = error.code === 'timeout' 
            ? `Network timeout loading ${contentTypeLabel}. Please try again.`
            : `Failed to load ${contentTypeLabel} content. Please try again.`;
        
        errorObservable(errorMessage);
        contentObservable('');
    }

    /**
     * Get color styles for a category
     */
    getCategoryColors(categoryId) {
        return this.metadataService.getCategoryColor(categoryId);
    }

    /**
     * Get metadata for a category from the loaded Firebase data
     * @param {string} categoryId - The category ID (e.g., "features", "configuration")
     * @returns {Object} Display metadata object with name, icon, description from Firebase
     */
    getCategoryMetadata(categoryId) {
        // Look in guides array first
        let category = (this.guidesArray() || []).find(g => g.id === categoryId);
        if (category) {
            return {
                name: category.title || categoryId,
                icon: category.icon || '📖',
                description: category.description || ''
            };
        }

        // Then look in FAQs array
        category = (this.faqsArray() || []).find(f => f.id === categoryId);
        if (category) {
            return {
                name: category.title || categoryId,
                icon: category.icon || '📖',
                description: category.description || ''
            };
        }

        // Fallback for unknown categories
        return { 
            name: categoryId, 
            icon: '📖', 
            description: '' 
        };
    }

    /**
     * Get the name of the category currently being loaded for display in loading message
     * Returns the guide name if loading guide, FAQ name if loading FAQ, or 'content' as fallback
     * 
     * @returns {string} Category name or 'content'
     */
    getCurrentLoadingCategoryName() {
        const guideId = this.selectedGuideCategory();
        const faqId = this.selectedFaqsCategory();
        
        if (guideId) {
            const guide = (this.guidesArray() || []).find(g => g.id === guideId);
            return guide?.title || guideId;
        }
        if (faqId) {
            const faq = (this.faqsArray() || []).find(f => f.id === faqId);
            return faq?.title || faqId;
        }
        return 'content';
    }

    /**
     * Update content when language changes
     */
    updateContentForLanguage() {
        // This will automatically update because observables are reactive
        // The template will re-render when currentLang changes
    }

    /**
     * Clear cache (useful when switching products)
     */
    clearCache() {
        this.cacheService.clearCache();
        this.metadataService.clearPersistedMetadata('guidebook-metadata');
        this.guideMetadata(null);
        this.guidesArray([]);
        this.faqsArray([]);
        this.guideLoadError(null);
        this.faqLoadError(null);
        this.selectedGuideContent('');
        this.selectedFaqContent('');
        this.selectedGuideCategory(null);
        this.selectedFaqsCategory(null);
        this.guideSearchQuery('');
        this.faqsSearchQuery('');
    }
}

export default GuideBookViewModel;